import React, { useEffect } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import CampaignStatusBadge from "../campaigns/CampaignStatusBadge";
import { fetchProductCampaigns } from "../../store/productsSlice";
import { formatCampaignDate } from "../../utils/campaignDisplayUtils";

const getProductId = (product) =>
    product?.id || product?.product_id || product?.productId || "";

const ProductCampaignsPanel = ({ product, organizationId, onViewCampaign }) => {
    const dispatch = useDispatch();
    const productId = getProductId(product);
    const {
        productCampaignsByProductId,
        productCampaignsStatusByProductId,
        productCampaignsErrorByProductId,
    } = useSelector((state) => state.products);

    const campaigns = productCampaignsByProductId[productId] || [];
    const status = productCampaignsStatusByProductId[productId] || "idle";
    const error = productCampaignsErrorByProductId[productId] || null;

    useEffect(() => {
        if (!productId) return;

        dispatch(fetchProductCampaigns({ productId, organizationId }));
    }, [dispatch, organizationId, productId]);

    const handleRetry = () => {
        if (!productId) return;

        dispatch(fetchProductCampaigns({ productId, organizationId }));
    };

    if (!productId) {
        return (
            <div className="m-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Select a product to view campaigns.
            </div>
        );
    }

    if (status === "idle" || status === "loading") {
        return (
            <div className="m-4 rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-800">
                <div className="flex items-center gap-2 font-semibold">
                    <Loader2 size={16} className="animate-spin" />
                    Loading campaigns...
                </div>
            </div>
        );
    }

    if (status === "failed") {
        return (
            <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <p className="font-semibold">
                    Unable to load campaigns for this product.
                </p>
                {error && <p className="mt-1">{error}</p>}
                <button
                    type="button"
                    onClick={handleRetry}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                >
                    <RefreshCw size={15} />
                    Retry
                </button>
            </div>
        );
    }

    if (campaigns.length === 0) {
        return (
            <div className="m-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No campaigns use this product yet.
            </div>
        );
    }

    return (
        <div>
            {campaigns.map((campaign) => {
                const campaignId = campaign?.id || campaign?.sequence_id;

                return (
                    <article
                        key={campaignId}
                        className="border-b border-slate-200 px-4 py-3 transition-colors odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h4
                                    className="truncate text-sm font-semibold text-slate-900"
                                    title={campaign?.name || ""}
                                >
                                    {campaign?.name || "Untitled campaign"}
                                </h4>
                                <p className="mt-1 text-xs text-slate-500">
                                    {formatCampaignDate(campaign?.created_at)}
                                </p>
                            </div>
                            <CampaignStatusBadge
                                status={campaign?.status}
                                campaign={campaign}
                                isPaused={
                                    campaign?.ui_helper_context?.is_paused
                                }
                            />
                        </div>

                        {campaign?.campaign_objective && (
                            <p
                                className="mt-3 line-clamp-3 text-sm leading-5 text-slate-600"
                                title={campaign.campaign_objective}
                            >
                                {campaign.campaign_objective}
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={() => onViewCampaign?.(campaignId)}
                            disabled={!campaignId}
                            className="mt-3 w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            View Campaign
                        </button>
                    </article>
                );
            })}
        </div>
    );
};

export default ProductCampaignsPanel;
